import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface AdminUser {
  id: string;
  phone: string;
  email?: string;
  role: 'admin' | 'super_admin';
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AdminUser | null;
  /** Pending 2FA challenge after a fresh password login. */
  requires2fa: boolean;
  /**
   * Short-lived token issued by `/auth/admin/login` when 2FA is required.
   * Exchanged (with a TOTP/backup code) at `/auth/2fa/login` for real tokens.
   * In-memory only — never persisted, cleared once the challenge completes.
   */
  challengeToken: string | null;
  /** True after a successful TOTP challenge in this session. */
  totpChallenged: boolean;

  setTokens: (access: string, refresh: string) => void;
  setUser: (user: AdminUser) => void;
  setRequires2fa: (value: boolean) => void;
  setChallengeToken: (token: string | null) => void;
  /** Reflect an enroll/disable outcome on the cached user. */
  markTotpChallenged: () => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      requires2fa: false,
      challengeToken: null,
      totpChallenged: false,

      setTokens: (access, refresh) => set({ accessToken: access, refreshToken: refresh }),
      setUser: (user) => set({ user }),
      setRequires2fa: (value) => set({ requires2fa: value }),
      setChallengeToken: (token) => set({ challengeToken: token }),
      markTotpChallenged: () =>
        set({ totpChallenged: true, requires2fa: false, challengeToken: null }),
      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          requires2fa: false,
          challengeToken: null,
          totpChallenged: false,
        }),
    }),
    {
      name: 'digipop-admin-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        // never persist totpChallenged — it must reset across browser sessions
      }),
    },
  ),
);

/** Selectors */
export const selectIsAuthed = (s: AuthState): boolean => Boolean(s.accessToken && s.user);
export const selectAccessToken = (s: AuthState): string | null => s.accessToken;
